/**
 * The lifecycle actions on one FamilyBiz listing (FR-7.2.2), shared by the queue
 * and the detail screen so the two cannot drift.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { Button, ErrorNotice, Field, Modal, Textarea } from "./primitives";
import {
  approveListing,
  reactivateListing,
  rejectListing,
  requestListingChanges,
  resolveOwnerRequest,
  suspendListing,
  unpublishListing,
  type ListingAdmin,
} from "../lib/api/familybiz";
import { confirmAction, useMutation } from "../lib/hooks";

type DialogKind =
  | "approve"
  | "reject"
  | "request-changes"
  | "resolve-approve"
  | "resolve-decline"
  | "suspend"
  | "unpublish";

/**
 * The buttons a listing's status allows, and the dialogs behind them.
 *
 * Rendered from one place so the queue and the detail screen offer the same set.
 * `suspend` and `unpublish` are both offered on a live listing because they are
 * different intents recorded differently in the audit log, even though both end at
 * `suspended`.
 */
export function ListingActions({
  listing: row,
  onChanged,
  showOpen = true,
}: {
  listing: ListingAdmin;
  /** Called with the server's message after any action succeeds. */
  onChanged: (message: string) => void;
  /** The link to the detail screen; off on the detail screen itself. */
  showOpen?: boolean;
}) {
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const onAct = setDialog;
  const act = useMutation(() => reactivateListing(row.id), {
    onSuccess: (result) => onChanged(result?.message ?? "Done."),
  });

  const isLive = row.status === "approved" || row.status === "disable_requested";
  // A version awaiting a decision: a first submission (`pending`), or an edit
  // to a live listing, which keeps its live status while it waits (FR-7.1.5).
  const awaitingReview = row.pending_version_id !== null;
  const isEdit = awaitingReview && row.published_version_id !== null;

  return (
    <div className="table__actions">
      {showOpen ? (
        <Link to={`/family-biz/${row.id}`} className="btn btn--ghost btn--sm">
          Open
        </Link>
      ) : null}

      {awaitingReview ? (
        <>
          <Button size="sm" variant="accent" onClick={() => onAct("approve")}>
            {isEdit ? "Approve edit" : "Approve"}
          </Button>
          <Button size="sm" onClick={() => onAct("request-changes")}>
            Changes
          </Button>
          <Button size="sm" variant="danger" onClick={() => onAct("reject")}>
            Reject
          </Button>
        </>
      ) : null}

      {row.requested_action ? (
        <>
          <Button size="sm" variant="accent" onClick={() => onAct("resolve-approve")}>
            Grant {row.requested_action}
          </Button>
          <Button size="sm" variant="danger" onClick={() => onAct("resolve-decline")}>
            Decline
          </Button>
        </>
      ) : null}

      {isLive ? (
        <>
          <Button
            size="sm"
            onClick={() => {
              if (confirmAction(`Take "${listingName(row)}" down? The owner keeps the listing and can request to be enabled again.`)) {
                onAct("unpublish");
              }
            }}
          >
            Take down
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (confirmAction(`Suspend "${listingName(row)}"? This is an admin action and is recorded as a suspension.`)) {
                onAct("suspend");
              }
            }}
          >
            Suspend
          </Button>
        </>
      ) : null}

      {row.status === "suspended" || row.status === "disabled" ? (
        <Button
          size="sm"
          variant="accent"
          disabled={act.pending}
          onClick={() => void act.run()}
        >
          Reactivate
        </Button>
      ) : null}

      {dialog ? (
        <ListingActionDialog
          listing={row}
          kind={dialog}
          onClose={() => setDialog(null)}
          onDone={(message) => {
            setDialog(null);
            onChanged(message);
          }}
        />
      ) : null}
    </div>
  );
}

function listingName(row: ListingAdmin): string {
  return (row.published ?? row.pending ?? row.draft)?.business_name ?? "this listing";
}

function ListingActionDialog({
  listing,
  kind,
  onClose,
  onDone,
}: {
  listing: ListingAdmin;
  kind: DialogKind;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [remarks, setRemarks] = useState("");
  const isEdit = listing.published_version_id !== null;

  const copy: Record<DialogKind, { title: string; body: string; confirm: string; needsRemarks: boolean }> = {
    approve: {
      title: isEdit ? "Approve this edit?" : "Approve this listing?",
      body: isEdit
        ? "The pending version replaces the live one in the member directory immediately."
        : "The pending version becomes the live one and appears in the member directory immediately.",
      confirm: "Approve",
      needsRemarks: false,
    },
    reject: {
      title: isEdit ? "Reject this edit?" : "Reject this listing?",
      body: isEdit
        ? "The live version stays as it is. The owner is told why, so make your note actionable."
        : "The owner is told why and can edit and resubmit. Your note is pushed to them, so make it actionable.",
      confirm: "Reject",
      needsRemarks: true,
    },
    "request-changes": {
      title: "Send this back for changes?",
      body: "The submission stays with the owner. Tell them exactly what to change - this note is the only message they get.",
      confirm: "Send back",
      needsRemarks: true,
    },
    "resolve-approve": {
      title: `Grant the owner's request to ${listing.requested_action ?? "change this listing"}?`,
      body: listing.requested_action === "disable"
        ? "The listing stops appearing in the directory."
        : "The listing becomes live in the directory again.",
      confirm: "Grant",
      needsRemarks: false,
    },
    "resolve-decline": {
      title: "Decline the owner's request?",
      body: "The listing keeps its current visibility. The owner is told why, so give them a reason.",
      confirm: "Decline",
      needsRemarks: true,
    },
    suspend: {
      title: "Suspend this listing?",
      body: "It is taken down as an admin action. The owner can ask to be enabled again.",
      confirm: "Suspend",
      needsRemarks: false,
    },
    unpublish: {
      title: "Take this listing down?",
      body: "It stops appearing in the directory. Recorded as an unpublish rather than a suspension.",
      confirm: "Take down",
      needsRemarks: false,
    },
  };

  const config = copy[kind];

  const mutation = useMutation(
    () => {
      switch (kind) {
        case "approve":
          return approveListing(listing.id, remarks.trim() || undefined);
        case "reject":
          return rejectListing(listing.id, remarks.trim());
        case "request-changes":
          return requestListingChanges(listing.id, remarks.trim());
        case "resolve-approve":
          return resolveOwnerRequest(listing.id, true, remarks.trim() || undefined);
        case "resolve-decline":
          return resolveOwnerRequest(listing.id, false, remarks.trim());
        case "suspend":
          return suspendListing(listing.id);
        case "unpublish":
          return unpublishListing(listing.id);
      }
    },
    { onSuccess: (result) => onDone(result?.message ?? "Done.") },
  );

  const blocked = config.needsRemarks && remarks.trim() === "";

  return (
    <Modal
      title={config.title}
      description={
        <>
          <strong>{listingName(listing)}</strong> · {listing.owner_name ?? "owner removed"}
          <br />
          {config.body}
        </>
      }
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <Field
          label={config.needsRemarks ? "Reason (required)" : "Remarks (optional)"}
          hint={config.needsRemarks ? "Pushed to the owner in the app." : "Recorded on the listing."}
        >
          {(id) => (
            <Textarea
              id={id}
              value={remarks}
              onChange={(event) => setRemarks(event.target.value)}
              maxLength={1000}
              disabled={mutation.pending}
            />
          )}
        </Field>

        <ErrorNotice error={mutation.error} />

        <div className="modal__actions">
          <Button onClick={onClose} disabled={mutation.pending}>
            Cancel
          </Button>
          <Button
            variant={config.confirm === "Reject" || config.confirm === "Suspend" ? "danger" : "accent"}
            disabled={blocked || mutation.pending}
            onClick={() => void mutation.run()}
          >
            {mutation.pending ? "Working…" : config.confirm}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
